import { useFormContext } from 'react-hook-form';
import { useEffect } from 'react';
import {
  CurrencyInputV2,
  DocumentNumberFieldV2,
  FormFieldV2,
  SearchFieldV2 as SearchField
} from '@/components/custom/erp';
import { khachHangSearchColumns, MA_CHUNG_TU } from '@/constants';
import { usePhieuXuatKhoSearchContext } from '../../context';
import { KhachHang, QuyenChungTu } from '@/types/schemas';
import { QUERY_KEYS } from '@/constants/query-keys';
import { Label } from '@/components/ui/label';
import { FormMode } from '@/types/form';

interface BasicInformationTabProps {
  formMode: FormMode;
  customer?: KhachHang | null;
  quyenChungTu?: QuyenChungTu | null;
  soChungTu?: string;
  setCustomer: (customer: KhachHang) => void;
  setQuyenChungTu: (documentBook: QuyenChungTu) => void;
  setSoChungTu: (numberDocument: string) => void;
}

export const BasicInformationTab = ({
  formMode,
  customer,
  quyenChungTu,
  soChungTu,
  setCustomer,
  setQuyenChungTu,
  setSoChungTu
}: BasicInformationTabProps) => {
  const isViewMode = formMode === 'view';
  const { watch, setValue } = useFormContext();
  const currentStatus = watch('status');
  const transactionCode = watch('ma_gd');
  const { hasInvoiceParams, transactionOptions } = usePhieuXuatKhoSearchContext();
  const disable = isViewMode || hasInvoiceParams;

  useEffect(() => {
    if (!transactionCode && transactionOptions[0]?.value) {
      setValue('ma_gd', transactionOptions[0].value);
    }
  }, [transactionCode, transactionOptions, setValue]);

  const allStatusOptions = [
    { value: '0', label: 'Lập chứng từ' },
    { value: '3', label: 'Chờ duyệt' },
    { value: '5', label: 'Xuất kho' },
    { value: '6', label: 'Duyệt phiếu yêu cầu xuất kho' },
    { value: '8', label: 'Xuất hóa đơn' }
  ];

  const selectableStatusOptions = allStatusOptions.filter(option => option.value !== '8' || currentStatus === '8');

  return (
    <div className='space-y-2 p-4'>
      <div className='flex flex-col gap-6 lg:flex-row lg:gap-8'>
        <div className='flex-1 space-y-2'>
          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Giao dịch</Label>
            <div className='w-[260px]'>
              <FormFieldV2 type='select' name='ma_gd' disabled={disable} options={transactionOptions} />
            </div>
          </div>

          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Mã đối tượng</Label>
            <SearchField<KhachHang>
              type='text'
              searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}`}
              searchColumns={khachHangSearchColumns}
              dialogTitle='Danh mục đối tượng'
              columnDisplay='customer_code'
              displayRelatedField='customer_name'
              value={customer?.customer_code || ''}
              relatedFieldValue={customer?.customer_name || ''}
              onRowSelection={setCustomer}
              disabled={disable}
            />
          </div>

          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Diễn giải</Label>
            <div className='flex-1'>
              <FormFieldV2 type='text' name='dien_giai' disabled={disable} />
            </div>
          </div>
        </div>

        <div className='w-full space-y-3 lg:w-[25rem]'>
          <DocumentNumberFieldV2
            ma_ct={MA_CHUNG_TU.TON_KHO.PHIEU_XUAT_KHO}
            onQuyenChungTuChange={setQuyenChungTu}
            soChungTu={soChungTu || ''}
            onSoChungTuChange={setSoChungTu}
            disabled={disable}
            classNameSearchField='flex-1'
            formMode={formMode}
          />

          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Ngày chứng từ</Label>
            <div className='flex-1'>
              <FormFieldV2 type='date' name='ngay_ct' disabled={disable} />
            </div>
          </div>

          <CurrencyInputV2 formMode={formMode} disabled={disable} />

          <div className='flex items-center gap-2'>
            <Label className='w-32 min-w-32 text-left'>Trạng thái</Label>
            <div className='flex-1'>
              <FormFieldV2 type='select' name='status' disabled={disable} options={selectableStatusOptions} />
            </div>
          </div>

          <div className='flex items-center gap-2'>
            <FormFieldV2
              type='checkbox'
              label='Dữ liệu được nhận'
              name='da_ghi_so'
              disabled={disable}
              className='flex items-center'
            />
          </div>
        </div>
      </div>
    </div>
  );
};
